import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, RotateCcw, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { JobPaymentControls } from "@/components/JobPaymentControls";
import { eur, fmtDate } from "@/lib/format";
import { fmtHMS, parseHMS } from "@/lib/jobTimer";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function CompletedJobs() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [toReopen, setToReopen] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", amount: "", date: "", time: "" });
  const [saving, setSaving] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expenses, setExpenses] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, exp] = await Promise.all([api.get("/jobs/completed"), api.get("/finance/expenses")]);
      setJobs(res.data.jobs);
      setTotal(res.data.total);
      setExpenses(Number(exp.data.value) || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inRange = (j) => {
    const d = (j.completed_date || "").slice(0, 10);
    if (fromDate && d < fromDate) return false;
    if (toDate && d > toDate) return false;
    return true;
  };
  const filtered = jobs.filter(inRange);
  // Only paid jobs count towards billing (regardless of efectivo/bizum).
  const billed = filtered.filter((j) => j.payment_status === "paid").reduce((s, j) => s + (Number(j.amount) || 0), 0);
  const earned = billed - (Number(expenses) || 0);
  const clearFilter = () => { setFromDate(""); setToDate(""); };
  const filterActive = fromDate || toDate;

  const saveExpenses = async (value) => {
    const v = parseFloat(String(value).replace(",", ".")) || 0;
    setExpenses(v);
    try {
      await api.put("/finance/expenses", { value: v });
    } catch (e) {
      toast.error("No se pudieron guardar los gastos");
    }
  };

  const patchJob = (id, fields) => {
    setJobs((prev) => prev.map((h) => h.id === id ? { ...h, ...fields } : h));
  };

  const reopen = async () => {
    const job = toReopen;
    setToReopen(null);
    setBusy(job.id);
    try {
      await api.post(`/jobs/${job.id}/reopen`);
      toast.success("Trabajo devuelto a pendientes");
      load();
    } catch (e) {
      toast.error("No se pudo actualizar");
    } finally {
      setBusy(null);
    }
  };

  const openEdit = (j) => {
    setEditing(j);
    setForm({ title: j.title, amount: j.amount != null ? String(j.amount) : "",
      date: (j.completed_date || j.date || "").slice(0, 10),
      time: fmtHMS(j.time_ms || 0) });
  };

  const saveEdit = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      await api.put(`/jobs/${editing.id}`, {
        title: form.title, client_id: editing.client_id, category: editing.category || "",
        amount: parseFloat(form.amount) || 0, date: form.date || null,
        time_ms: parseHMS(form.time),
      });
      toast.success("Trabajo actualizado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="completed-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Trabajos realizados</h1>
          <p className="text-muted-foreground mt-1">Historial completo de todo lo realizado</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 shrink-0">
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Realizados</p>
            <p className="font-display text-3xl font-bold text-primary" data-testid="completed-total">{filterActive ? filtered.length : total}</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total facturado</p>
            <p className="font-display text-3xl font-bold" data-testid="completed-billed">{eur(billed)}</p>
            <p className="text-[10px] text-muted-foreground">solo pagados</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Gastos</p>
            <Input type="number" inputMode="decimal" value={expenses === 0 ? "" : expenses}
              onChange={(e) => setExpenses(e.target.value)}
              onBlur={(e) => saveExpenses(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              data-testid="expenses-input"
              className="w-28 ml-auto text-right font-display text-lg font-bold h-10" placeholder="0,00" />
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Ganado</p>
            <p className={`font-display text-3xl font-bold ${earned < 0 ? "text-destructive" : "text-green-600"}`} data-testid="completed-earned">{eur(earned)}</p>
          </div>
        </div>
      </div>

      {/* Date range filter */}
      <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-card p-4">
        <div>
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            data-testid="completed-date-from" className="w-full sm:w-44" />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            data-testid="completed-date-to" className="w-full sm:w-44" />
        </div>
        {filterActive && (
          <Button variant="outline" size="sm" onClick={clearFilter} data-testid="completed-clear-filter">
            Quitar filtro
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : jobs.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-muted-foreground">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
          Aún no has completado ningún trabajo.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-muted-foreground">
          No hay trabajos realizados en esas fechas.
        </div>
      ) : (
        <div className="space-y-2" data-testid="completed-list">
          {filtered.map((j) => (
            <div key={j.id} data-testid={`completed-item-${j.id}`}
              className="rounded-md border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{j.title}</p>
                  <div className="flex flex-wrap gap-x-3 text-sm text-muted-foreground mt-0.5">
                    <span>{j.client_name}</span>
                    <span>· {fmtDate(j.completed_date)}</span>
                    {j.category && <span className="text-primary">· {j.category}</span>}
                    {j.time_ms > 0 && <span className="font-mono">· ⏱ {fmtHMS(j.time_ms)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-display font-bold" data-testid={`completed-amount-${j.id}`}>{eur(j.amount)}</span>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(j)} data-testid={`edit-completed-${j.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setToReopen(j)} disabled={busy === j.id}
                    data-testid={`reopen-job-${j.id}`} title="Devolver a pendientes">
                    {busy === j.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border">
                <JobPaymentControls job={j} onChange={(fields) => patchJob(j.id, fields)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-completed-dialog">
          <DialogHeader><DialogTitle>Editar trabajo realizado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título / descripción</Label>
              <Textarea value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="edit-job-title" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Dinero cobrado (€)</Label>
                <Input type="number" inputMode="decimal" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="edit-job-amount" />
              </div>
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="edit-job-date" />
              </div>
            </div>
            <div>
              <Label>Tiempo dedicado (HH:MM:SS)</Label>
              <Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="edit-job-time" placeholder="00:00:00" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-edit-job">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toReopen} onOpenChange={(o) => !o && setToReopen(null)}>
        <AlertDialogContent data-testid="reopen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Devolver a pendientes?</AlertDialogTitle>
            <AlertDialogDescription>
              El trabajo "{toReopen?.title}" volverá a la lista de trabajos pendientes y se quitará del historial hasta que lo marques de nuevo como realizado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={reopen} data-testid="confirm-reopen">Devolver a pendientes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
